import { useState } from "react";
import {
  Box,
  Button,
  Flex,
  HStack,
  Text,
  chakra,
  IconButton,
  Drawer,
  Portal,
  CloseButton,
  VStack,
} from "@chakra-ui/react";
import { useNavigate, useLocation } from "react-router-dom";
import { BsWhatsapp } from "react-icons/bs";
import { FiArrowUpRight, FiMenu, FiHome, FiCalendar, FiBookOpen, FiCompass, FiChevronRight, FiFileText, FiCpu } from "react-icons/fi";
import { keyframes } from "@emotion/react";

import { useSiteSettingsQuery } from "@/hooks/useSupabaseQueries";

type RouteLink = {
  label: string;
  href: string;
  type: "route";
};

type AnchorLink = {
  label: string;
  href: string;
  type: "anchor";
};

export type NavigationLink = RouteLink | AnchorLink;

export const navLinks: NavigationLink[] = [
  { label: "Home", href: "/", type: "route" },
  { label: "Booking", href: "/booking", type: "route" },
  { label: "Courses", href: "/courses", type: "route" },
  { label: "Gemstones", href: "/gemstones", type: "route" },
  { label: "Research & Dev", href: "/research", type: "route" },
  { label: "Blog", href: "/blog", type: "route" },
  { label: "Contact", href: "/contact", type: "route" },
  // { label: "Support", href: "/#support", type: "anchor" },
];

export const legalLinks = [
  { label: "Privacy Policy", href: "/p/privacy-policy" },
  { label: "Terms & Conditions", href: "/p/terms-and-conditions" },
];
const textShine = keyframes`
  to {
    background-position: 200% center;
  } 
`;


export const SaturnLogo = ({ size = "36px" }: { size?: string }) => {
  return (
    <Box
      position="relative"
      w={size}
      h={size}
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <svg
        viewBox="0 0 64 64"
        width="100%"
        height="100%"
        style={{ overflow: "visible" }}
      >
        <defs>
          <radialGradient id="saturn-body" cx="30%" cy="30%" r="70%">
            <stop offset="0%" stopColor="#FFE082" />
            <stop offset="35%" stopColor="#FFB300" />
            <stop offset="75%" stopColor="#E65100" />
            <stop offset="100%" stopColor="#3E2723" />
          </radialGradient>

          <linearGradient id="saturn-ring" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="rgba(255, 215, 0, 0.85)" />
            <stop offset="50%" stopColor="rgba(255, 143, 0, 0.95)" />
            <stop offset="100%" stopColor="rgba(93, 64, 55, 0.85)" />
          </linearGradient>
        </defs>

        {/* Ambient atmospheric glow */}
        <circle
          cx="32"
          cy="32"
          r="16"
          fill="rgba(255, 179, 0, 0.15)"
          filter="blur(5px)"
        />

        {/* Back half of Saturn's rings (tilted at -15 degrees) */}
        <ellipse
          cx="32"
          cy="32"
          rx="27"
          ry="9"
          fill="none"
          stroke="url(#saturn-ring)"
          strokeWidth="3.6"
          transform="rotate(-15 32 32)"
          opacity="0.85"
        />
        <ellipse
          cx="32"
          cy="32"
          rx="23"
          ry="7.5"
          fill="none"
          stroke="url(#saturn-ring)"
          strokeWidth="1"
          transform="rotate(-15 32 32)"
          opacity="0.5"
        />

        {/* Main planet sphere */}
        <circle
          cx="32"
          cy="32"
          r="14.5"
          fill="url(#saturn-body)"
          style={{ filter: "drop-shadow(0 3px 8px rgba(0,0,0,0.5))" }}
        />

        {/* Front half of Saturn's rings (rendered above planet sphere) */}
        <path
          d="M 5.8 25 A 27 9 0 0 0 58.2 39"
          fill="none"
          stroke="url(#saturn-ring)"
          strokeWidth="3.6"
          strokeLinecap="round"
          transform="rotate(-15 32 32)"
        />
        <path
          d="M 9.8 26.5 A 23 7.5 0 0 0 54.2 37.5"
          fill="none"
          stroke="url(#saturn-ring)"
          strokeWidth="1"
          strokeLinecap="round"
          transform="rotate(-15 32 32)"
          opacity="0.65"
        />

        {/* Planetary surface banding lines */}
        <path
          d="M 18.5 28 Q 32 31 45.5 28"
          fill="none"
          stroke="rgba(255, 255, 255, 0.12)"
          strokeWidth="1.2"
        />
        <path
          d="M 19.5 35 Q 32 38 44.5 35"
          fill="none"
          stroke="rgba(0, 0, 0, 0.15)"
          strokeWidth="1.6"
        />
      </svg>
    </Box>
  );
};

export const NavigationBar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const { data: settings } = useSiteSettingsQuery();
  const phoneGeneral = settings?.find((s: any) => s.key === "phone_general")?.value || "919459412030";
  const brandLogoUrl = settings?.find((s: any) => s.key === "brand_logo_url")?.value || "";

  const mobileNavItems = [
    { label: "Home", subtitle: "Celestial gateway & Overview", href: "/", icon: FiHome },
    { label: "Book Consultation", subtitle: "Naadi & Jaimini guidance", href: "/booking", icon: FiCalendar },
    { label: "Courses", subtitle: "Mentorship & Lifetime learning", href: "/courses", icon: FiBookOpen },
    { label: "Gemstones", subtitle: "Certified & Energised crystals", href: "/gemstones", icon: FiCompass },
    { label: "Research & Dev", subtitle: "Astro-Tech & Vedic research", href: "/research", icon: FiCpu },
    { label: "Blog", subtitle: "Latest celestial insights", href: "/blog", icon: FiFileText },
    { label: "Contact", subtitle: "Get in touch with us", href: "/contact", icon: BsWhatsapp },
  ];

  return (
    <Box
      as="header"
      position="fixed"
      top={0}
      w="full"
      zIndex={50}
      px={{ base: 4, md: 6 }}
      py={{ base: 3, md: 4 }}
    >
      <Box
        border="1px solid"
        borderColor="whiteAlpha.200"
        bg="rgba(12, 12, 12, 0.7)"
        backdropFilter="blur(24px)"
        borderRadius="full"
        maxW="container.xl"
        mx="auto"
        px={{ base: 3, md: 6 }}
        py={{ base: 2, md: 3 }}
        boxShadow="0 20px 50px rgba(0,0,0,0.45)"
        position="relative"
        overflow="hidden"
      >
        <chakra.span
          position="absolute"
          inset="0"
          borderRadius="full"
          border="1px solid"
          borderColor="whiteAlpha.200"
          opacity={0.7}
          pointerEvents="none"
        />
        <Flex
          position="relative"
          align="center"
          justify="space-between"
          gap={{ base: 3, lg: 6 }}
        >
          <HStack gap={{ base: 3, md: 8 }} flex={1} minW={0}>
            <HStack cursor={"pointer"} onClick={() => navigate("/")}>
              {brandLogoUrl ? (
                <img
                  src={brandLogoUrl}
                  alt="AstroSatvik Logo"
                  style={{
                    width: "34px",
                    height: "34px",
                    objectFit: "cover",
                    borderRadius: "50%",
                    border: "1px solid rgba(255, 215, 0, 0.4)",
                    boxShadow: "0 0 8px rgba(255, 215, 0, 0.2)",
                  }}
                />
              ) : (
                <SaturnLogo size="34px" />
              )}
              <Text
                fontSize={{ base: "xs", md: "xl" }}
                fontWeight="extrabold"
                letterSpacing={{ base: "0.14em", md: "0.22em" }}
                fontFamily="heading"
                whiteSpace="nowrap"
                background="linear-gradient(90deg, #FFD700, #FFC107, #c18b0d, #FFD700)"
                backgroundSize="200% auto"
                backgroundClip="text"
                color="transparent"
                animation={`${textShine} 4s linear infinite`}
              >
                ASTROSATVIK
              </Text>
            </HStack>
          </HStack>
          <HStack gap={2} justify="flex-end">
            <HStack
              display={{ base: "none", lg: "flex" }}
              gap={1.5}
              fontSize="xs"
              letterSpacing="widest"
              textTransform="uppercase"
            >
              {navLinks.map((link) =>
                link.type === "route" ? (
                  <Text
                    cursor={"pointer"}
                    key={link.href}
                    onClick={() => navigate(link.href)}
                    px={4}
                    py={2}
                    borderRadius="full"
                    color="whiteAlpha.800"
                    fontWeight="semibold"
                    _hover={{ bg: "whiteAlpha.100", color: "gold" }}
                  >
                    {link.label}
                  </Text>
                ) : (
                  <Text
                    cursor={"pointer"}
                    key={link.href}
                    px={4}
                    py={2}
                    onClick={() => navigate(link.href)}
                    borderRadius="full"
                    color="whiteAlpha.800"
                    fontWeight="semibold"
                    _hover={{ bg: "whiteAlpha.100", color: "gold" }}
                  >
                    {link.label}
                  </Text>
                ),
              )}
            </HStack>

            {/* Desktop Book Call Button */}
            <Button
              bgGradient="linear(to-r, gold, primary)"
              color="#3c2f00"
              size="sm"
              display={{ base: "none", md: "inline-flex" }}
              fontWeight="bold"
              borderRadius="full"
              px={6}
              boxShadow="0 10px 30px rgba(244,201,94,0.4)"
              onClick={() => navigate("/booking")}
              _hover={{
                transform: "translateY(-1px)",
                boxShadow: "0 12px 35px rgba(244,201,94,0.5)",
              }}
            >
              <FiArrowUpRight />
              Book a Call
            </Button>

            {/* Premium Mobile Hamburger Menu Button */}
            <IconButton
              aria-label="Open navigation menu"
              display={{ base: "inline-flex", lg: "none" }}
              onClick={() => setMenuOpen(true)}
              variant="plain"
              color="whiteAlpha.900"
              borderRadius="full"
              bg="whiteAlpha.50"
              border="1px solid"
              borderColor="whiteAlpha.100"
              _hover={{
                bg: "rgba(255, 215, 0, 0.08)",
                color: "gold",
                borderColor: "rgba(255, 215, 0, 0.3)",
                transform: "scale(1.05)",
                boxShadow: "0 0 15px rgba(255, 215, 0, 0.15)",
              }}
              _active={{
                transform: "scale(0.95)",
                bg: "rgba(255, 215, 0, 0.15)",
              }}
              size="sm"
              h="36px"
              w="36px"
              transition="all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)"
            >
              <FiMenu size="18px" />
            </IconButton>
          </HStack>
        </Flex>
      </Box>

      {/* Premium Mobile Menu Drawer */}
      <Drawer.Root open={menuOpen} onOpenChange={(e) => setMenuOpen(e.open)} placement="start">
        <Portal>
          <Drawer.Backdrop backdropFilter="blur(8px)" bg="rgba(0,0,0,0.4)" />
          <Drawer.Positioner zIndex={2000}>
            <Drawer.Content
              bg="rgba(12, 12, 12, 0.96)"
              borderLeft="1px solid"
              borderColor="whiteAlpha.100"
              backdropFilter="blur(24px)"
              color="white"
              maxW="xs"
              w="min(18rem, 90vw)"
              h="100vh"
              p={0}
            >
              <Drawer.Header borderBottomWidth="1px" borderColor="whiteAlpha.100" py={4} px={4} display="flex" alignItems="center" justifyContent="space-between">
                <HStack gap={3}>
                  {brandLogoUrl ? (
                    <img
                      src={brandLogoUrl}
                      alt="AstroSatvik Logo"
                      style={{
                        width: "30px",
                        height: "30px",
                        objectFit: "cover",
                        borderRadius: "50%",
                        border: "1px solid rgba(255, 215, 0, 0.4)",
                      }}
                    />
                  ) : (
                    <SaturnLogo size="30px" />
                  )}
                  <Text
                    fontSize="md"
                    fontWeight="extrabold"
                    letterSpacing="0.1em"
                    background="linear-gradient(90deg, #FFD700, #FFC107)"
                    backgroundClip="text"
                    color="transparent"
                  >
                    ASTROSATVIK
                  </Text>
                </HStack>
                <Drawer.CloseTrigger asChild>
                  <CloseButton color="gold" size="sm" borderRadius="full" _hover={{ bg: "whiteAlpha.100" }} />
                </Drawer.CloseTrigger>
              </Drawer.Header>

              <Drawer.Body py={6} px={4}>
                <VStack gap={5} align="stretch">
                  {mobileNavItems.map((item) => {
                    const isActive = location.pathname === item.href;
                    const IconComponent = item.icon;
                    return (
                      <Box
                        key={item.href}
                        onClick={() => {
                          navigate(item.href);
                          setMenuOpen(false);
                        }}
                        cursor="pointer"
                        borderRadius="xl"
                        p={3.5}
                        border="1px solid"
                        borderColor={isActive ? "rgba(255, 215, 0, 0.25)" : "transparent"}
                        bg={isActive ? "rgba(255, 215, 0, 0.05)" : "whiteAlpha.50"}
                        _hover={{
                          bg: "rgba(255, 215, 0, 0.08)",
                          borderColor: "rgba(255, 215, 0, 0.2)",
                          transform: "translateX(4px)",
                          transition: "all 0.25s ease",
                        }}
                        transition="all 0.2s ease"
                      >
                        <Flex align="center" gap={3}>
                          <Box
                            p={2.5}
                            borderRadius="lg"
                            bg={isActive ? "gold" : "whiteAlpha.100"}
                            color={isActive ? "#3c2f00" : "gold"}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <IconComponent size="18px" />
                          </Box>
                          <Box flex={1}>
                            <Text
                              fontSize="sm"
                              fontWeight="bold"
                              color={isActive ? "gold" : "white"}
                            >
                              {item.label}
                            </Text>
                            <Text fontSize="2xs" color="whiteAlpha.600" mt={0.5}>
                              {item.subtitle}
                            </Text>
                          </Box>
                          <FiChevronRight size="14px" color="rgba(255, 215, 0, 0.4)" />
                        </Flex>
                      </Box>
                    );
                  })}

                  <Box borderBottom="1px solid" borderColor="whiteAlpha.100" my={2} />

                  {/* Dynamic Support WhatsApp CTA inside mobile drawer */}
                  <chakra.a
                    href={`https://wa.me/${phoneGeneral}?text=Hi%20AstroSatvik%2C%20I'd%20like%20to%20inquire%20about%20your%20services.`}
                    target="_blank"
                    rel="noopener noreferrer"
                    _hover={{ textDecoration: "none" }}
                  >
                    <Box
                      p={4}
                      borderRadius="xl"
                      bgGradient="linear(to-br, rgba(37, 211, 102, 0.1), rgba(18, 140, 126, 0.05))"
                      border="1px solid"
                      borderColor="rgba(37, 211, 102, 0.2)"
                      _hover={{
                        borderColor: "rgba(37, 211, 102, 0.4)",
                        transform: "translateY(-1px)",
                      }}
                      transition="all 0.2s"
                    >
                      <Flex align="center" gap={3}>
                        <BsWhatsapp color="#25D366" size="20px" />
                        <Box>
                          <Text fontSize="xs" fontWeight="bold" color="#25D366">
                            WhatsApp Support
                          </Text>
                          <Text fontSize="3xs" color="whiteAlpha.500" mt={0.5}>
                            Connect instantly with our team
                          </Text>
                        </Box>
                      </Flex>
                    </Box>
                  </chakra.a>
                </VStack>
              </Drawer.Body>
            </Drawer.Content>
          </Drawer.Positioner>
        </Portal>
      </Drawer.Root>
    </Box>
  );
};
